import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import React, { useOptimistic, startTransition } from 'react';
import { useActivateAccountMutation, useDeactivateAccountMutation } from '@/pages/hainok/Accounts/accountsApi';
import type { Account } from '@/types/accounts.types';

type ActionsDialogProps = {
  actionsDialogOpen: boolean;
  setActionsDialogOpen: (open: boolean) => void;
  selectedAccountForActions?: Account | null;
};

type OptimisticAction = {
  type: 'activate' | 'deactivate';
};

export const ActionsDialog: React.FC<ActionsDialogProps> = ({
  actionsDialogOpen,
  setActionsDialogOpen,
  selectedAccountForActions,
}) => {
  const [activateAccount, { isLoading: isActivating }] = useActivateAccountMutation();
  const [deactivateAccount, { isLoading: isDeactivating }] = useDeactivateAccountMutation();

  // Use optimistic updates for account activation status
  const [optimisticAccount, addOptimisticUpdate] = useOptimistic(
    selectedAccountForActions,
    (currentAccount, action: OptimisticAction) => {
      if (!currentAccount) return currentAccount;
      return { ...currentAccount, isActive: action.type === 'activate' };
    },
  );

  const handleAccountActivation = async (accountId: string, currentIsActive: boolean) => {
    const action: OptimisticAction = {
      type: currentIsActive ? 'deactivate' : 'activate',
    };

    // Perform the actual API call in a transition
    startTransition(() => {
      // Optimistically update the UI
      addOptimisticUpdate(action);
    });

    try {
      if (currentIsActive) await deactivateAccount({ accountId }).unwrap();
      else await activateAccount({ accountId }).unwrap();
    } catch (error) {
      console.error('Failed to update account status:', error);
      // The error will be handled by RTK Query, and the cache will be invalidated
      // causing a refetch that will restore the correct state
    }
  };

  if (!optimisticAccount) {
    return null;
  }

  return (
    <Dialog open={actionsDialogOpen} onOpenChange={setActionsDialogOpen}>
      <DialogContent className='bg-slate-800 border-slate-700 text-white max-w-2xl w-[90vw] sm:max-w-2xl sm:w-[90vw] max-h-[80vh] overflow-y-scroll flex flex-col'>
        <DialogHeader>
          <DialogTitle>Account Actions</DialogTitle>
          <DialogDescription className='text-slate-400'>
            Manage actions for "{optimisticAccount.name}"
          </DialogDescription>
        </DialogHeader>

        <div className='py-6 space-y-6'>
          {/* Account Actions */}
          <div className='bg-slate-700/50 rounded-lg p-4 space-y-4'>
            <h3 className='text-lg font-semibold text-white'>Account Status</h3>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-white font-medium'>
                  {optimisticAccount.isActive ? 'Deactivate Account' : 'Activate Account'}
                </p>
                <p className='text-sm text-slate-400'>
                  {optimisticAccount.isActive
                    ? 'This will disable the account and prevent all users from accessing the system'
                    : 'This will enable the account and allow users to access the system'}
                </p>
              </div>
              <Button
                variant={optimisticAccount.isActive ? 'destructive' : 'default'}
                onClick={() => handleAccountActivation(optimisticAccount._id, optimisticAccount.isActive)}
                disabled={isActivating || isDeactivating}
                className='ml-4'
              >
                {optimisticAccount.isActive
                  ? `${isDeactivating ? 'Deactivating...' : 'Deactivate'}`
                  : `${isActivating ? 'Activating...' : 'Activate'}`}
              </Button>
            </div>
          </div>

          {/* Additional Actions Placeholder */}
          <div className='bg-slate-700/50 rounded-lg p-4 space-y-4'>
            <h3 className='text-lg font-semibold text-white'>Additional Actions</h3>
            <div className='text-slate-400'>
              <p>Additional account management actions will be implemented here...</p>
              <ul className='list-disc list-inside mt-2 text-sm space-y-1'>
                <li>Edit account details</li>
                <li>Manage subscription</li>
                <li>View audit logs</li>
                <li>Reset account settings</li>
              </ul>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
